import { message } from 'antd';
import { cloneDeep } from 'lodash';

import api from '@/api';
import { MODES_TITLES } from '@/components/MiningAndPumping/SummaryMode/constants';
import { getDataFromLocalStorage, putDataIntoLocalStorage } from '@/lib/localStorage';
import { select } from '@/store';
import { OBJECT_TYPE } from '@/components/MiningAndPumping/SummaryMode/constants';

const INDICATORS_TEMPLATE_PDB_KEY = 'summary-mode/characteristics';

const defaultVersion = {
    id: 0,
    title: 'Основная',
};

const defaultIndicators = [33, 34, 35, 36, 37, 38, 39, 40, 41, 43, 46, 47, 59];

const summaryModeModel = {
    name: 'summaryMode',
    state: {
        init: true,
        drawerVisible: true,
        chartsWidgetVisible: true,
        tableWidgetVisible: true,
        currentMode: MODES_TITLES.developmentIndicators,
        currentModal: 'none',
        gridApi: null,

        settingsSummary: null,
        summaryIndicators: null,
        wellList: [],

        version: null,
        monthPeriod: 12,
        checkedFields: null,
        checkedStrarums: null,
        loading: {
            settingsSummary: false,
            summaryIndicators: false,
            wellList: false,
        },
    },
    reducers: {
        'miningAndPumping/setSummaryMode': state => {
            return { ...state, settingsSummary: null };
        },

        'setInit'(state, payload) {
            return { ...state, init: payload };
        },
        'setDrawerVisible'(state, payload) {
            return { ...state, drawerVisible: payload };
        },
        'setChartsWidgetVisible'(state, payload) {
            return { ...state, chartsWidgetVisible: payload };
        },
        'setTableWidgetVisible'(state, payload) {
            return { ...state, tableWidgetVisible: payload };
        },
        'setCurrentMode'(state, payload) {
            return { ...state, currentMode: payload };
        },
        'setCurrentModal'(state, payload) {
            return { ...state, currentModal: payload };
        },

        'setSettingsSummary'(state, payload) {
            return { ...state, settingsSummary: payload };
        },
        'setSummaryIndicators'(state, payload) {
            return { ...state, summaryIndicators: payload };
        },
        'setGridApi'(state, payload) {
            return { ...state, gridApi: payload };
        },
        'setVersion'(state, payload) {
            return { ...state, version: payload };
        },
        'setMonthPeriod'(state, payload) {
            return { ...state, monthPeriod: payload };
        },
        'setCheckedFields'(state, payload) {
            return { ...state, checkedFields: payload };
        },
        'setCheckedStratums'(state, payload) {
            return { ...state, checkedStratums: payload };
        },
        'setWellList'(state, payload) {
            return { ...state, wellList: payload };
        },

        /* loading */
        'setSettingsSummaryLoading'(state, payload) {
            return {
                ...state,
                loading: {
                    ...state.loading,
                    settingsSummary: payload,
                },
            };
        },
        'setWellListLoading'(state, payload) {
            return {
                ...state,
                loading: {
                    ...state.loading,
                    wellList: payload,
                },
            };
        },
        'setSummaryIndicatorsLoading'(state, payload) {
            return {
                ...state,
                loading: {
                    ...state.loading,
                    summaryIndicators: payload,
                },
            };
        },
    },

    effects: dispatch => ({
        setSummaryIndicators(payload) {
            if (payload) {
                putDataIntoLocalStorage({ key: INDICATORS_TEMPLATE_PDB_KEY, data: payload });
            }
        },

        async fetchSettingsSummary(props, rootState) {
            this.setSettingsSummaryLoading(true);
            const { calculationObject } = rootState.tree;
            const selection = select(models => ({
                activeGroup: models.lists.activeGroup,
            }));
            const { activeGroup } = selection(rootState);
            console.log('fetchSettingsSummary', calculationObject);
            const payload = {};
            if (calculationObject && OBJECT_TYPE[calculationObject?.node]) {
                if (calculationObject?.node !== 'field') {
                    payload[OBJECT_TYPE[calculationObject?.node]] = calculationObject?.id;
                } else {
                    payload.fieldIds = [calculationObject?.id];
                    payload.ventureId = calculationObject?.parentVentureId;
                }
            }
            if (!calculationObject && activeGroup?.children?.length) {
                const wellIds = activeGroup?.children.map(well => well.id);
                payload.wellIds = wellIds;
            }
            if ((calculationObject && calculationObject?.node === 'group') || calculationObject?.node === 'shaft') {
                const wellIds = calculationObject.children.map(child => child.id);
                payload.wellIds = wellIds;
            }

            try {
                const data = await api.miningAndPumping.fetchSettingsSummary(payload);
                if (!Object.keys(data?.fields)?.length)
                    message.info(
                        'Отсутствуют настройки для суммарного режима. Попробуйте изменить версию технологических показателей или выбрать другой объект разработки.',
                    );
                this.setSettingsSummary(data);

                if (data?.versions?.length) this.setVersion(defaultVersion);

                const fields = data?.fields;

                const checkedFields = Object.keys(fields).reduce((acc, fieldId) => ({ ...acc, [fieldId]: true }), {});
                this.setCheckedFields(checkedFields);

                const checkedStratums = Object.keys(fields)?.reduce((acc, fieldId) => {
                    const { id, stratums } = fields[fieldId];
                    const normalizedStratums = stratums.reduce(
                        (acc, { key, title, children }) => [...acc, { key, title, children }, ...children],
                        [],
                    );
                    return { ...acc, [id]: [...normalizedStratums] };
                }, {});
                this.setCheckedStratums(checkedStratums);
            } catch (error) {
                console.error(error);
                this.clearDataForModes();
                message.error('Не удалось получить настройки для суммарного режима');
            }
            this.setSettingsSummaryLoading(false);
        },

        async fetchSummaryIndicators(payload = {}) {
            this.setSummaryIndicatorsLoading(true);
            this.setSummaryIndicators(null);
            try {
                const indicatorsFromPDB = getDataFromLocalStorage({ key: INDICATORS_TEMPLATE_PDB_KEY });
                let indicators = indicatorsFromPDB?.data;

                if (!indicators) {
                    indicators = await api.miningAndPumping.fetchSummaryIndicators(payload);
                }

                this.setSummaryIndicators(indicators);
            } catch (error) {
                console.error(error);
                message.error('Не удалось получить показатели для суммарного режима');
            }
            this.setSummaryIndicatorsLoading(false);
        },

        async fetchWellList(payload) {
            this.setWellListLoading(true);
            this.setWellList(null);
            try {
                const data = await api.miningAndPumping.fetchWellList(payload);
                const refactoredData = Object.keys(data).reduce(
                    (acc, key) => ({ ...acc, [key]: { ...data[key], checked: true } }),
                    {},
                );
                this.setWellList(refactoredData);
            } catch (error) {
                console.error(error);
                message.error('Не удалось получить список скважин');
            }
            this.setWellListLoading(false);
        },

        setDefaultIndicators(payload, rootState) {
            const copyIndicators = cloneDeep(rootState.summaryMode.summaryIndicators);
            if (copyIndicators) {
                Object.keys(copyIndicators).forEach(key => {
                    if (defaultIndicators.some(defKey => defKey === +key)) {
                        copyIndicators[key].tableChecked = true;
                        copyIndicators[key].chartChecked = true;
                    } else {
                        copyIndicators[key].tableChecked = false;
                        copyIndicators[key].chartChecked = false;
                    }
                });
                this.setSummaryIndicators(copyIndicators);
            }
        },

        clearDataForModes() {
            dispatch.developmentIndicators.setData(null);
            dispatch.comparisonOfPeriods.setData(null);
            dispatch.approximation.setData(null);
            dispatch.consolidatedProduction.setData(null);
        },

        clearDependencies() {
            this.setCheckedFields(null);
            this.setCheckedStratums(null);
            this.setSettingsSummary(null);
            this.setSummaryIndicators(null);
            this.setWellList(null);
            this.clearDataForModes();

            dispatch.versions.setActiveVersions([]);
            // dispatch.versions.saveActiveVersions();
            this.setInit(true);
        },
    }),

    selectors: (slice, createSelector) => ({
        activeFields() {
            return createSelector(
                rootState => rootState.summaryMode.checkedFields,
                checkedFields => {
                    return Object.keys(checkedFields ?? [])
                        .filter(fieldId => checkedFields[fieldId])
                        .map(item => +item);
                },
            );
        },

        // activeStratums() {
        //     return createSelector(
        //         rootState => rootState.summaryMode.checkedFields,
        //         rootState => rootState.summaryMode.checkedStratums,
        //         (checkedFields, checkedStratums) => {
        //             return checkedFields && checkedStratums
        //                 ? [
        //                       ...new Set(
        //                           Object.keys(checkedFields ?? [])
        //                               .filter(fieldId => checkedFields[fieldId])
        //                               .reduce(
        //                                   (acc, fieldId) => [
        //                                       ...acc,
        //                                       ...(checkedStratums[fieldId] ?? [])
        //                                           .filter(({ children }) => children === undefined)
        //                                           .map(({ key }) => +key),
        //                                   ],
        //                                   []
        //                               )
        //                       ),
        //                   ]
        //                 : [];
        //         }
        //     );
        // },
        //TODO
        // Оставить старый селектор до полной проверки всех режимов,где он может использоваться
        activeStratums() {
            return createSelector(
                state => state.summaryMode.checkedFields,
                state => state.summaryMode.checkedStratums,
                (checkedFields, checkedStratums) => {
                    if (!checkedFields || !checkedStratums) return [];

                    return [
                        ...new Set(
                            Object.entries(checkedFields)
                                .filter(([_, isChecked]) => isChecked) // Only checked fields
                                .flatMap(
                                    ([fieldId]) =>
                                        (checkedStratums[fieldId] ?? [])
                                            .filter(stratum => !stratum.children) // Exclude parents
                                            .map(stratum => stratum.key.toString().split('-')[0]) // Extract before '-'
                                            .filter(key => !isNaN(key)) // Ensure it's a valid number
                                            .map(Number), // Convert to number
                                ),
                        ),
                    ];
                },
            );
        },
    }),
};

export default summaryModeModel;
